import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavbarProps {
  /** Force transparent mode (no scroll-triggered bg). Used on hero sections that handle their own bg. */
  alwaysTransparent?: boolean;
  /** Opacity value passed from parent (e.g. HeroSection parallax fade) */
  opacity?: number;
  /** Extra pointer-events class for the wrapper — needed when hero has pointer-events:none */
  pointerEventsNone?: boolean;
}

const navLinks = [
  { label: 'ABOUT US', to: '/about' },
  { label: 'OUR SERVICES', to: '/services' },
  { label: 'OUR PRODUCTS', to: '/products' },
  { label: 'CONTACT US', to: '/contact' },
];

export default function Navbar({ opacity = 1, pointerEventsNone = false }: NavbarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-50 flex justify-center px-8 pt-4 transition-all duration-300 ${pointerEventsNone ? 'pointer-events-none' : ''}`}
      style={{ opacity }}
    >
      {/* Glass island */}
      <nav
        className="w-full flex items-center justify-between px-8 py-3 rounded-2xl pointer-events-auto"
        style={{
          background: 'rgba(15, 15, 15, 0.5)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.12)',
          boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
        }}
      >
        {/* Logo */}
        <Link
          to="/"
          className="boldonse text-white text-xl tracking-tight hover:text-[#F2CF59] transition-colors duration-300 whitespace-nowrap"
        >
          Syntera
        </Link>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.to;
            return (
              <Link
                key={link.label}
                to={link.to}
                className="text-sm font-medium tracking-widest transition-colors duration-300 whitespace-nowrap"
                style={{ color: isActive ? '#F2CF59' : 'rgba(255,255,255,0.85)' }}
              >
                {link.label}
              </Link>
            );
          })}
        </div>

        {/* CTA button */}
        <Link
          to="/contact"
          className="hidden md:flex px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 hover:scale-105 whitespace-nowrap"
          style={{ background: '#F2CF59', color: '#0d0d0d' }}
        >
          Get in Touch
        </Link>

        {/* Mobile hamburger */}
        <button
          className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer whitespace-nowrap"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-white text-2xl`} />
        </button>
      </nav>

      {/* Mobile menu panel — drops below the island */}
      {menuOpen && (
        <div
          className="absolute top-full mt-2 left-8 right-8 flex flex-col rounded-2xl overflow-hidden pointer-events-auto"
          style={{
            background: 'rgba(15,15,15,0.85)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.to}
              onClick={() => setMenuOpen(false)}
              className="px-8 py-4 text-sm font-medium tracking-widest text-white/80 hover:text-[#F2CF59] hover:bg-white/5 transition-colors duration-200 border-b border-white/5 whitespace-nowrap"
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/contact"
            onClick={() => setMenuOpen(false)}
            className="px-8 py-4 text-sm font-semibold text-center whitespace-nowrap"
            style={{ background: '#F2CF59', color: '#0d0d0d' }}
          >
            Get in Touch
          </Link>
        </div>
      )}
    </div>
  );
}